import { PlaybackOption, Playlist, Song } from '@repo/types'
import { Button, CloseIcon, Heading, IconButton, List, merge } from '@repo/ui'
import { TextInput } from '@repo/ui/client'
import { ChangeEventHandler, useDeferredValue, useState } from 'react'

import { useDialogContext } from '@/dialogs/DialogContext'
import { SpotifyAuthButton } from '@/store/integrations/spotify/components/SpotifyAuthButton'
import { addFeaturesToSong } from '@/store/integrations/spotify/converters/convertSong'
import { useSpotify } from '@/store/integrations/spotify/useSpotify'

import { checkDuplicates } from './duplicate-checker/checkDuplicates'
import { useQuerySpotifyTracks } from '../../../store/integrations/spotify/queries/useQuerySpotifyTracks'

interface AddTrackProps {
  onClickAdd: (song: Song) => void
  playlist: Playlist
}

export function AddMoreSongs({ onClickAdd, playlist }: AddTrackProps) {
  const { auth } = useSpotify()

  return (
    <div className='flex flex-col gap-4 pt-6'>
      <Heading>Add More Songs</Heading>

      {auth.isAuthenticated ? (
        <SearchSpotify playlist={playlist} onClickAdd={onClickAdd} />
      ) : (
        <div className='mr-auto'>
          <SpotifyAuthButton />
        </div>
      )}
    </div>
  )
}

interface SearchSpotifyProps {
  onClickAdd: (song: Song) => void
  playlist: Playlist
}

function SearchSpotify({ onClickAdd, playlist }: SearchSpotifyProps) {
  const { playlistService } = useSpotify()

  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const { setConfirmPrompt, setErrorDialog } = useDialogContext()
  const { data = [] } = useQuerySpotifyTracks(deferredQuery)

  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    setQuery(event.target.value)
  }

  const handleClickClear = () => {
    setQuery('')
  }

  const addSong = async (song: Song) => {
    if (!song.spotifyMeta) return

    const features = await playlistService.queryTrackFeatures(song.spotifyMeta.track.id)

    const updatedSong = addFeaturesToSong(song, features)

    onClickAdd(updatedSong)

    setQuery('')
  }

  const handleClickAdd = async (song: Song) => {
    if (!song.spotifyMeta) return

    const result = checkDuplicates(song, playlist.songs, {
      isSpotifyCompatible: playlist.playbackOptions.includes(PlaybackOption.SPOTIFY)
    })

    if (result.status === 'error') {
      setErrorDialog({
        error: result.message,
        title: result.title
      })
      return
    }

    if (result.status === 'warning') {
      setConfirmPrompt({
        description: result.message,
        heading: result.title,
        onConfirm: () => addSong(song)
      })
      return
    }

    addSong(song)
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex w-full items-center gap-2'>
        <div className='flex w-full max-w-md flex-col gap-2'>
          <TextInput
            label='Search Spotify'
            name='song-query'
            value={query}
            onChange={handleChange}
          />
        </div>
        <div
          className={merge(
            { 'opacity-0 pointer-events-none': !query },
            'relative -left-12 flex items-center mt-8 dark:bg-shade-2 bg-shade-3 transition-opacity'
          )}
        >
          <IconButton size='small' title='Clear' onClick={handleClickClear}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <Results data={data} handleClickAdd={handleClickAdd} />
    </div>
  )
}

interface ResultsProps {
  data: Song[]
  handleClickAdd: (song: Song) => void
}

function Results({ data, handleClickAdd }: ResultsProps) {
  return (
    <>
      {Boolean(data.length) && (
        <List>
          {data.map(song => (
            <List.Item
              actions={
                <>
                  <Button text='Add' onClick={() => handleClickAdd(song)} />
                </>
              }
              className='flex items-center justify-between'
              key={song.id}
            >
              <div className='flex w-full flex-col justify-center gap-0.5 overflow-hidden'>
                <span className='truncate leading-tight'>{song.title}</span>
                <span className='truncate text-sm font-semibold uppercase leading-tight tracking-tight text-muted'>
                  {song.artist}
                </span>
                <span className='truncate text-xs leading-tight tracking-wide text-muted'>
                  {song?.album}
                </span>
              </div>
            </List.Item>
          ))}
        </List>
      )}
    </>
  )
}
